import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Calendar, 
  Clock, 
  MapPin,
  User,
  Phone,
  Mail,
  Search
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import './Appointments.css';

const Appointments = () => {
  const [appointments, setAppointments] = useState([
    {
      id: 1,
      doctor: 'Dr. Sarah Johnson',
      specialty: 'Cardiology',
      date: '2024-01-15',
      time: '14:30',
      duration: 30,
      location: 'Medical Center',
      address: '123 Health St, City',
      phone: '+1 (555) 123-4567',
      email: 'dr.johnson@medical.com',
      notes: 'Follow-up appointment for heart condition',
      status: 'confirmed'
    },
    {
      id: 2,
      doctor: 'Dr. Michael Chen',
      specialty: 'Dermatology',
      date: '2024-01-18',
      time: '10:00',
      duration: 45,
      location: 'Skin Clinic',
      address: '456 Beauty Ave, City',
      phone: '+1 (555) 987-6543',
      email: 'dr.chen@skinclinic.com',
      notes: 'Annual skin check',
      status: 'confirmed'
    },
    {
      id: 3,
      doctor: 'Dr. Emily Davis',
      specialty: 'Dentistry',
      date: '2024-01-20',
      time: '16:00',
      duration: 60,
      location: 'Dental Care',
      address: '789 Smile Blvd, City',
      phone: '+1 (555) 456-7890',
      email: 'dr.davis@dentalcare.com',
      notes: 'Regular cleaning and checkup',
      status: 'pending'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    doctor: '',
    specialty: '',
    date: '',
    time: '',
    duration: 30,
    location: '',
    address: '',
    phone: '',
    email: '',
    notes: ''
  });

  const handleAddAppointment = () => {
    if (!formData.doctor || !formData.date || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    const newAppointment = {
      id: Date.now(),
      ...formData,
      status: 'pending'
    };

    setAppointments([...appointments, newAppointment]);
    setFormData({
      doctor: '',
      specialty: '',
      date: '',
      time: '',
      duration: 30,
      location: '',
      address: '',
      phone: '',
      email: '',
      notes: ''
    });
    setShowAddForm(false);
    toast.success('Appointment added successfully!');
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      doctor: appointment.doctor,
      specialty: appointment.specialty,
      date: appointment.date,
      time: appointment.time,
      duration: appointment.duration,
      location: appointment.location,
      address: appointment.address,
      phone: appointment.phone,
      email: appointment.email,
      notes: appointment.notes
    });
    setShowAddForm(true);
  };

  const handleUpdateAppointment = () => {
    if (!formData.doctor || !formData.date || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAppointments(appointments.map(apt => 
      apt.id === editingAppointment.id ? { ...apt, ...formData } : apt
    ));
    
    setEditingAppointment(null);
    setFormData({
      doctor: '',
      specialty: '',
      date: '',
      time: '',
      duration: 30,
      location: '',
      address: '',
      phone: '',
      email: '',
      notes: ''
    });
    setShowAddForm(false);
    toast.success('Appointment updated successfully!');
  };

  const handleDeleteAppointment = (id) => {
    setAppointments(appointments.filter(apt => apt.id !== id));
    toast.success('Appointment deleted successfully!');
  };

  const filteredAppointments = appointments.filter(appointment => {
    const matchesSearch = appointment.doctor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         appointment.specialty.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         appointment.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || appointment.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'primary';
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">Appointments</h1>
        <p className="page-subtitle">Schedule and manage your medical appointments</p>
      </div>

      <div className="appointments-container">
        <div className="appointments-header">
          <div className="appointments-filters">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search appointments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            <Plus size={16} />
            Schedule Appointment
          </button>
        </div>

        {showAddForm && (
          <div className="add-appointment-form">
            <h3>{editingAppointment ? 'Edit Appointment' : 'Schedule New Appointment'}</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Doctor Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.doctor}
                  onChange={(e) => setFormData({...formData, doctor: e.target.value})}
                  placeholder="e.g., Dr. Sarah Johnson"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Specialty</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.specialty}
                  onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                  placeholder="e.g., Cardiology"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Time *</label>
                <input
                  type="time"
                  className="form-input"
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Duration (minutes)</label>
                <select
                  className="form-input"
                  value={formData.duration}
                  onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="e.g., Medical Center"
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="e.g., 123 Health St, City"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="e.g., +1 (555) 123-4567"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="e.g., doctor@clinic.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes..."
                rows="3"
              />
            </div>

            <div className="form-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingAppointment(null);
                  setFormData({
                    doctor: '',
                    specialty: '',
                    date: '',
                    time: '',
                    duration: 30,
                    location: '',
                    address: '',
                    phone: '',
                    email: '',
                    notes: ''
                  });
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={editingAppointment ? handleUpdateAppointment : handleAddAppointment}
              >
                {editingAppointment ? 'Update' : 'Schedule'} Appointment
              </button>
            </div>
          </div>
        )}

        <div className="appointments-list">
          {filteredAppointments.map((appointment) => (
            <div key={appointment.id} className="appointment-card">
              <div className="appointment-header">
                <div className="appointment-date">
                  <div className="date-day">
                    {format(new Date(appointment.date), 'dd')}
                  </div>
                  <div className="date-month">
                    {format(new Date(appointment.date), 'MMM')}
                  </div>
                </div>
                
                <div className="appointment-info">
                  <h3 className="appointment-doctor">{appointment.doctor}</h3>
                  <p className="appointment-specialty">{appointment.specialty}</p>
                  <div className="appointment-time">
                    <Clock size={16} />
                    {appointment.time} ({appointment.duration} min)
                  </div>
                </div>
                
                <div className="appointment-status">
                  <span className={`badge badge-${getStatusColor(appointment.status)}`}>
                    {appointment.status}
                  </span>
                </div>
              </div>

              <div className="appointment-details">
                <div className="detail-row">
                  <MapPin size={16} />
                  <span>{appointment.location}</span>
                </div>
                
                {appointment.address && (
                  <div className="detail-row">
                    <MapPin size={16} />
                    <span>{appointment.address}</span>
                  </div>
                )}
                
                {appointment.phone && (
                  <div className="detail-row">
                    <Phone size={16} />
                    <span>{appointment.phone}</span>
                  </div>
                )}
                
                {appointment.email && (
                  <div className="detail-row">
                    <Mail size={16} />
                    <span>{appointment.email}</span>
                  </div>
                )}
              </div>

              {appointment.notes && (
                <div className="appointment-notes">
                  <strong>Notes:</strong> {appointment.notes}
                </div>
              )}

              <div className="appointment-actions">
                <button
                  className="action-btn"
                  onClick={() => handleEditAppointment(appointment)}
                >
                  <Edit size={16} />
                  Edit
                </button>
                
                <button
                  className="action-btn delete"
                  onClick={() => handleDeleteAppointment(appointment.id)}
                >
                  <Trash2 size={16} />
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredAppointments.length === 0 && (
          <div className="empty-state">
            <Calendar size={64} className="empty-state-icon" />
            <h3>No appointments found</h3>
            <p>Schedule your first appointment to get started</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              <Plus size={16} />
              Schedule Appointment
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Appointments; 